/**
 * useMonitorWallets Hook
 *
 * Handles syncing of monitor wallet addresses between localStorage and Supabase.
 * Supports FluxNode Monitor, Cronos Monitor, and Hedera Monitor.
 *
 * Features:
 * - Works offline with localStorage
 * - Syncs to Supabase when user is logged in
 * - Automatic conflict resolution (cloud wins on initial load)
 * - Debounced sync to avoid excessive API calls
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase/client';
import { trackWalletLinked, trackWalletUnlinked } from '../services/analyticsTracker';
import { scheduleDeviceSync } from '../services/deviceSyncService';

// Types
type MonitorType = 'flux' | 'cronos' | 'hedera' | 'thegraph' | 'algorand' | 'cardano' | 'xdc' | 'vechain' | 'icp' | 'axelar';

interface WalletData {
  wallet_address: string;
  label?: string;
  display_order?: number;
  metadata?: Record<string, unknown>;
}

interface FluxPurchaseTracking {
  [nodeId: string]: {
    purchasePrice: number;
    purchaseDate?: string;
  };
}

// Node aliases - keyed by nodeId (txhash:outidx)
interface FluxNodeAliases {
  [nodeId: string]: string;
}

interface MonitorWalletsState {
  wallets: string[];
  isLoading: boolean;
  isSyncing: boolean;
  error: Error | null;
  lastSynced: Date | null;
}

interface UseMonitorWalletsOptions {
  monitorType: MonitorType;
  localStorageKey: string;
  // FluxNode specific
  purchaseTrackingKey?: string;
  appsOwnerKey?: string;
  nodeAliasesKey?: string;
}

interface UseMonitorWalletsReturn extends MonitorWalletsState {
  addWallet: (address: string, label?: string) => void;
  removeWallet: (address: string) => void;
  setWallets: (wallets: string[]) => void;
  syncNow: () => Promise<void>;
  restoreFromCloud: () => Promise<boolean>; // Restore from cloud (for new device)
  isLoggedIn: boolean;
  // FluxNode specific
  purchaseTracking?: FluxPurchaseTracking;
  setPurchaseTracking?: (tracking: FluxPurchaseTracking) => void;
  appsOwner?: string;
  setAppsOwner?: (owner: string) => void;
  nodeAliases?: FluxNodeAliases;
  setNodeAliases?: (aliases: FluxNodeAliases) => void;
}

// Debounce delay for syncing (ms)
const SYNC_DEBOUNCE = 2000;

/**
 * Hook for managing monitor wallets with localStorage + Supabase sync
 */
export function useMonitorWallets(options: UseMonitorWalletsOptions): UseMonitorWalletsReturn {
  const { monitorType, localStorageKey, purchaseTrackingKey, appsOwnerKey, nodeAliasesKey } = options;
  const { isAuthenticated } = useAuthStore();
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // State
  const [wallets, setWalletsState] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);

  // FluxNode specific state
  const [purchaseTracking, setPurchaseTrackingState] = useState<FluxPurchaseTracking>({});
  const [appsOwner, setAppsOwnerState] = useState<string>('');
  const [nodeAliases, setNodeAliasesState] = useState<FluxNodeAliases>({});

  // Refs for debouncing
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadDoneRef = useRef(false);

  // Get access token from Supabase (more reliable than store)
  useEffect(() => {
    const getToken = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setAccessToken(session?.access_token || null);
      } catch {
        setAccessToken(null);
      }
    };

    if (isAuthenticated) {
      getToken();
    } else {
      setAccessToken(null);
    }

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAccessToken(session?.access_token || null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [isAuthenticated, monitorType]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(localStorageKey);
      // Check for valid JSON (not null, not "undefined", not empty)
      if (saved && saved !== 'undefined' && saved !== 'null' && saved.trim()) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      console.error(`[Monitor] Error loading ${localStorageKey}:`, e);
      // Clear corrupted data
      localStorage.removeItem(localStorageKey);
    }
    return [];
  }, [localStorageKey]);

  // Save to localStorage
  const saveToLocalStorage = useCallback((data: string[]) => {
    try {
      if (data.length > 0) {
        localStorage.setItem(localStorageKey, JSON.stringify(data));
      } else {
        localStorage.removeItem(localStorageKey);
      }
    } catch (e) {
      console.error(`[Monitor] Error saving ${localStorageKey}:`, e);
    }
  }, [localStorageKey]);

  // Load FluxNode specific data from localStorage
  const loadFluxExtras = useCallback(() => {
    if (monitorType !== 'flux') return { tracking: {}, owner: '', aliases: {} };

    let tracking: FluxPurchaseTracking = {};
    let owner = '';
    let aliases: FluxNodeAliases = {};

    try {
      if (purchaseTrackingKey) {
        const savedTracking = localStorage.getItem(purchaseTrackingKey);
        if (savedTracking) {
          tracking = JSON.parse(savedTracking);
        }
      }
      if (appsOwnerKey) {
        owner = localStorage.getItem(appsOwnerKey) || '';
      }
      if (nodeAliasesKey) {
        const savedAliases = localStorage.getItem(nodeAliasesKey);
        if (savedAliases) {
          aliases = JSON.parse(savedAliases);
        }
      }
    } catch (e) {
      console.error('[Monitor] Error loading Flux extras:', e);
    }

    return { tracking, owner, aliases };
  }, [monitorType, purchaseTrackingKey, appsOwnerKey, nodeAliasesKey]);

  // Save FluxNode specific data to localStorage
  const saveFluxExtras = useCallback((tracking: FluxPurchaseTracking, owner: string, aliases: FluxNodeAliases = {}) => {
    if (monitorType !== 'flux') return;

    try {
      if (purchaseTrackingKey) {
        localStorage.setItem(purchaseTrackingKey, JSON.stringify(tracking));
      }
      if (appsOwnerKey && owner) {
        localStorage.setItem(appsOwnerKey, owner);
      }
      if (nodeAliasesKey) {
        localStorage.setItem(nodeAliasesKey, JSON.stringify(aliases));
      }
    } catch (e) {
      console.error('[Monitor] Error saving Flux extras:', e);
    }
  }, [monitorType, purchaseTrackingKey, appsOwnerKey, nodeAliasesKey]);

  // Sync to Supabase
  const syncToCloud = useCallback(async (
    walletsToSync: string[],
    trackingToSync?: FluxPurchaseTracking,
    ownerToSync?: string,
    aliasesToSync?: FluxNodeAliases
  ) => {
    if (!accessToken) {
      return;
    }

    // Ensure walletsToSync is a valid array
    const safeWallets = Array.isArray(walletsToSync) ? walletsToSync : [];

    setIsSyncing(true);

    try {
      const body: Record<string, unknown> = {
        action: 'sync',
        monitor_type: monitorType,
        wallets: safeWallets.map((addr, idx) => ({
          wallet_address: addr,
          display_order: idx,
        })),
      };

      // Add FluxNode extras
      if (monitorType === 'flux') {
        if (trackingToSync) {
          body.purchase_tracking = Object.entries(trackingToSync).map(([nodeId, data]) => ({
            node_id: nodeId,
            purchase_price: data.purchasePrice,
            purchase_date: data.purchaseDate,
          }));
        }
        if (ownerToSync !== undefined) {
          body.apps_owner = ownerToSync;
        }
        if (aliasesToSync) {
          body.node_aliases = Object.entries(aliasesToSync).map(([nodeId, alias]) => ({
            node_id: nodeId,
            alias: alias,
          }));
          console.log('[Monitor] Syncing aliases to cloud:', body.node_aliases);
        }
      }

      const response = await fetch('/api/monitor/wallets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Sync failed');
      }

      setLastSynced(new Date());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Sync failed'));
    } finally {
      setIsSyncing(false);
    }
  }, [accessToken, monitorType]);

  // Debounced sync
  const debouncedSync = useCallback((
    walletsToSync: string[],
    trackingToSync?: FluxPurchaseTracking,
    ownerToSync?: string,
    aliasesToSync?: FluxNodeAliases
  ) => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      syncToCloud(walletsToSync, trackingToSync, ownerToSync, aliasesToSync);
    }, SYNC_DEBOUNCE);
  }, [syncToCloud]);

  // Fetch from cloud
  const fetchFromCloud = useCallback(async (): Promise<{
    wallets: string[];
    tracking?: FluxPurchaseTracking;
    owner?: string;
    aliases?: FluxNodeAliases;
  } | null> => {
    if (!accessToken) {
      return null;
    }

    try {
      const response = await fetch('/api/monitor/wallets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          action: 'get',
          monitor_type: monitorType,
        }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      const result: {
        wallets: string[];
        tracking?: FluxPurchaseTracking;
        owner?: string;
        aliases?: FluxNodeAliases;
      } = {
        wallets: data.wallets?.map((w: WalletData) => w.wallet_address) || [],
      };

      // Parse FluxNode extras
      if (monitorType === 'flux') {
        if (data.purchase_tracking) {
          result.tracking = {};
          for (const t of data.purchase_tracking) {
            result.tracking[t.node_id] = {
              purchasePrice: parseFloat(t.purchase_price),
              purchaseDate: t.purchase_date,
            };
          }
        }
        result.owner = data.apps_owner || '';
        if (data.node_aliases) {
          result.aliases = {};
          for (const a of data.node_aliases) {
            result.aliases[a.node_id] = a.alias;
          }
        }
        console.log('[Monitor] Fetched from cloud - aliases:', result.aliases);
      }

      return result;
    } catch {
      return null;
    }
  }, [accessToken, monitorType]);

  // Initial load - merge localStorage with cloud data
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);

      // Load from localStorage first
      const localWallets = loadFromLocalStorage();
      const fluxExtras = loadFluxExtras();

      // Set initial state from localStorage
      setWalletsState(localWallets);
      if (monitorType === 'flux') {
        setPurchaseTrackingState(fluxExtras.tracking);
        setAppsOwnerState(fluxExtras.owner);
        setNodeAliasesState(fluxExtras.aliases);
      }

      // If logged in, try to sync with cloud
      if (accessToken && !initialLoadDoneRef.current) {
        initialLoadDoneRef.current = true;

        try {
          const cloudData = await fetchFromCloud();

          if (cloudData && cloudData.wallets.length > 0) {
            // If localStorage is empty, restore from cloud
            if (localWallets.length === 0) {
              setWalletsState(cloudData.wallets);
              saveToLocalStorage(cloudData.wallets);

              // Restore FluxNode extras
              if (monitorType === 'flux') {
                if (cloudData.tracking) {
                  setPurchaseTrackingState(cloudData.tracking);
                  if (purchaseTrackingKey) {
                    localStorage.setItem(purchaseTrackingKey, JSON.stringify(cloudData.tracking));
                  }
                }
                if (cloudData.owner) {
                  setAppsOwnerState(cloudData.owner);
                  if (appsOwnerKey) {
                    localStorage.setItem(appsOwnerKey, cloudData.owner);
                  }
                }
                if (cloudData.aliases) {
                  setNodeAliasesState(cloudData.aliases);
                  if (nodeAliasesKey) {
                    localStorage.setItem(nodeAliasesKey, JSON.stringify(cloudData.aliases));
                  }
                }
              }

              setLastSynced(new Date());
            } else {
              // If localStorage has data, push it to cloud as backup
              await syncToCloud(localWallets, fluxExtras.tracking, fluxExtras.owner, fluxExtras.aliases);
              setLastSynced(new Date());
            }
          } else if (localWallets.length > 0) {
            // No cloud data but local data exists - push to cloud
            await syncToCloud(localWallets, fluxExtras.tracking, fluxExtras.owner, fluxExtras.aliases);
            setLastSynced(new Date());
          }
        } catch {
          // Cloud sync failed, using localStorage
        }
      }

      setIsLoading(false);
    };

    load();
  }, [accessToken]);

  // Public methods
  const addWallet = useCallback((address: string, _label?: string) => {
    setWalletsState(prev => {
      if (prev.includes(address)) return prev;
      const newWallets = [...prev, address];
      saveToLocalStorage(newWallets);
      debouncedSync(newWallets, purchaseTracking, appsOwner, nodeAliases);
      // V2.M.2 — emit link_wallet event (fire-and-forget, never throws).
      void trackWalletLinked(monitorType, address);
      // V2.N.4 — mirror local wallets to DB (debounced).
      scheduleDeviceSync();
      return newWallets;
    });
  }, [saveToLocalStorage, debouncedSync, purchaseTracking, appsOwner, nodeAliases, monitorType]);

  const removeWallet = useCallback((address: string) => {
    setWalletsState(prev => {
      const newWallets = prev.filter(w => w !== address);
      saveToLocalStorage(newWallets);
      debouncedSync(newWallets, purchaseTracking, appsOwner, nodeAliases);
      // V2.M.2 — emit unlink_wallet event.
      void trackWalletUnlinked(monitorType, address);
      // V2.N.4 — mirror local wallets to DB (debounced).
      scheduleDeviceSync();
      return newWallets;
    });
  }, [saveToLocalStorage, debouncedSync, purchaseTracking, appsOwner, nodeAliases, monitorType]);

  const setWallets = useCallback((newWallets: string[]) => {
    setWalletsState(newWallets);
    saveToLocalStorage(newWallets);
    debouncedSync(newWallets, purchaseTracking, appsOwner, nodeAliases);
  }, [saveToLocalStorage, debouncedSync, purchaseTracking, appsOwner, nodeAliases]);

  const syncNow = useCallback(async () => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    await syncToCloud(wallets, purchaseTracking, appsOwner, nodeAliases);
  }, [syncToCloud, wallets, purchaseTracking, appsOwner, nodeAliases]);

  // FluxNode specific setters
  const setPurchaseTracking = useCallback((tracking: FluxPurchaseTracking) => {
    setPurchaseTrackingState(tracking);
    saveFluxExtras(tracking, appsOwner, nodeAliases);
    debouncedSync(wallets, tracking, appsOwner, nodeAliases);
  }, [saveFluxExtras, debouncedSync, wallets, appsOwner, nodeAliases]);

  const setAppsOwner = useCallback((owner: string) => {
    setAppsOwnerState(owner);
    saveFluxExtras(purchaseTracking, owner, nodeAliases);
    debouncedSync(wallets, purchaseTracking, owner, nodeAliases);
  }, [saveFluxExtras, debouncedSync, wallets, purchaseTracking, nodeAliases]);

  const setNodeAliases = useCallback((aliases: FluxNodeAliases) => {
    setNodeAliasesState(aliases);
    saveFluxExtras(purchaseTracking, appsOwner, aliases);
    debouncedSync(wallets, purchaseTracking, appsOwner, aliases);
  }, [saveFluxExtras, debouncedSync, wallets, purchaseTracking, appsOwner]);

  // Restore from cloud (for new device or manual restore)
  const restoreFromCloud = useCallback(async (): Promise<boolean> => {
    if (!accessToken) {
      return false;
    }

    setIsLoading(true);

    try {
      const cloudData = await fetchFromCloud();

      if (cloudData && cloudData.wallets.length > 0) {
        // Restore wallets from cloud
        setWalletsState(cloudData.wallets);
        saveToLocalStorage(cloudData.wallets);

        // Restore FluxNode extras
        if (monitorType === 'flux') {
          if (cloudData.tracking) {
            setPurchaseTrackingState(cloudData.tracking);
          }
          if (cloudData.owner) {
            setAppsOwnerState(cloudData.owner);
          }
          if (cloudData.aliases) {
            setNodeAliasesState(cloudData.aliases);
          }
          saveFluxExtras(cloudData.tracking || {}, cloudData.owner || '', cloudData.aliases || {});
        }

        setLastSynced(new Date());
        setIsLoading(false);
        return true;
      }

      setIsLoading(false);
      return false;
    } catch (e) {
      console.error('[Monitor] Restore from cloud error:', e);
      setError(e instanceof Error ? e : new Error('Restore failed'));
      setIsLoading(false);
      return false;
    }
  }, [accessToken, fetchFromCloud, saveToLocalStorage, saveFluxExtras, monitorType]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // Build return object
  const result: UseMonitorWalletsReturn = {
    wallets,
    isLoading,
    isSyncing,
    error,
    lastSynced,
    addWallet,
    removeWallet,
    setWallets,
    syncNow,
    restoreFromCloud,
    isLoggedIn: !!accessToken,
  };

  // Add FluxNode specific fields
  if (monitorType === 'flux') {
    result.purchaseTracking = purchaseTracking;
    result.setPurchaseTracking = setPurchaseTracking;
    result.appsOwner = appsOwner;
    result.setAppsOwner = setAppsOwner;
    result.nodeAliases = nodeAliases;
    result.setNodeAliases = setNodeAliases;
  }

  return result;
}

// Convenience hooks for each monitor type
export function useFluxNodeWallets() {
  return useMonitorWallets({
    monitorType: 'flux',
    localStorageKey: 'fluxnode_wallets',
    purchaseTrackingKey: 'fluxnode_purchase_tracking',
    appsOwnerKey: 'fluxnode_apps_owner',
    nodeAliasesKey: 'fluxnode_node_aliases',
  });
}

export function useCronosWallets() {
  return useMonitorWallets({
    monitorType: 'cronos',
    localStorageKey: 'cronos_wallets',
  });
}

export function useHederaMonitorWallets() {
  return useMonitorWallets({
    monitorType: 'hedera',
    localStorageKey: 'hedera_accounts',
  });
}

export function useTheGraphMonitorWallets() {
  return useMonitorWallets({
    monitorType: 'thegraph',
    localStorageKey: 'thegraph_wallets',
  });
}

export function useAlgorandMonitorWallets() {
  return useMonitorWallets({
    monitorType: 'algorand',
    localStorageKey: 'algorand_addresses',
  });
}

export function useCardanoMonitorWallets() {
  return useMonitorWallets({
    monitorType: 'cardano',
    localStorageKey: 'cardano_addresses',
  });
}

export function useXdcMonitorWallets() {
  return useMonitorWallets({
    monitorType: 'xdc',
    localStorageKey: 'xdc_addresses',
  });
}

export function useVechainMonitorWallets() {
  return useMonitorWallets({
    monitorType: 'vechain',
    localStorageKey: 'vechain_addresses',
  });
}

export function useIcpMonitorWallets() {
  return useMonitorWallets({
    monitorType: 'icp',
    localStorageKey: 'icp_accounts',
  });
}

export function useAxelarMonitorWallets() {
  return useMonitorWallets({
    monitorType: 'axelar',
    localStorageKey: 'axelar_addresses',
  });
}
